/**
 * TrialBattleScreen Component
 *
 * Handles the actual trial battle, calling the server for battle simulation
 * and displaying results. Manages victory/defeat flow.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Trophy,
  X,
  ChevronRight,
  Sparkles,
  Award,
  AlertTriangle,
  RotateCcw
} from 'lucide-react';
import { useGame } from '../contexts/GameContext';
import { useInventory } from '../contexts/InventoryContext';
import { useAuth } from '../contexts/AuthContext';
import { fetchTrialData } from '../data/elite4Trials';
import { TypeBadge } from '../components/TypeIcon';
import { apiProcessTrialBattle, apiAwardTrialStamp } from '../services/apiService';
import { PokemonSprite, generatePokemonSprite, getBaseSpecies } from '../utils/gameUtils';
import BattleScreen from './BattleScreen';

const TrialBattleScreen = () => {
  const { setGameState, trialState, setTrialState, battleState, setBattleState, battleSpeed } = useGame();
  const { refreshPrimos } = useInventory();
  const { token } = useAuth();

  const [battlePhase, setBattlePhase] = useState('preparing'); // preparing, battling, result
  const [battleResult, setBattleResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trialRegions, setTrialRegions] = useState({});
  const [trialRewards, setTrialRewards] = useState({ primos: 1000 });
  const [dataLoaded, setDataLoaded] = useState(false);

  // Fetch trial data on mount
  useEffect(() => {
    const loadData = async () => {
      const data = await fetchTrialData();
      if (data) {
        setTrialRegions(data.regions || {});
        setTrialRewards(data.rewards || { primos: 1000 });
      }
      setDataLoaded(true);
    };
    loadData();
  }, []);

  const region = trialState?.regionKey ? trialRegions[trialState.regionKey] : null;
  const currentMemberIndex = trialState?.currentMemberIndex || 0;
  const currentMember = region?.members?.[currentMemberIndex];
  const playerPokemon = trialState?.currentPokemon;
  const opponent = trialState?.opponent;

  // Start battle on mount (wait for data to be loaded)
  useEffect(() => {
    if (dataLoaded && battlePhase === 'preparing' && playerPokemon && opponent) {
      startBattle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded, battlePhase, playerPokemon, opponent]);

  // Watch for battle completion (when log is fully played)
  useEffect(() => {
    if (battlePhase === 'battling' && battleState?.serverResult && battleState?.isTrialBattle) {
      const log = battleState.log || [];
      const logIndex = battleState.logIndex ?? 0;

      // Check if battle is complete (log fully played and someone's HP is 0)
      if (logIndex >= log.length - 1 &&
          (battleState.player?.currentHP <= 0 || battleState.opponent?.currentHP <= 0)) {
        // Small delay to let the final animation show
        const timer = setTimeout(() => {
          handleBattleComplete();
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battlePhase, battleState?.logIndex, battleState?.player?.currentHP, battleState?.opponent?.currentHP, battleState?.isTrialBattle]);

  const startBattle = async () => {
    setLoading(true);
    setError(null);

    try {
      // Call server to process trial battle
      const result = await apiProcessTrialBattle(
        {
          regionKey: trialState.regionKey,
          memberIndex: currentMemberIndex,
          playerPokemonId: playerPokemon.id,
          playerPokemon: {
            name: playerPokemon.name,
            type: playerPokemon.type,
            stats: playerPokemon.stats,
            abilities: playerPokemon.abilities || []
          },
          opponent: opponent
        },
        token
      );

      if (result?.success === false) {
        setError(result.error || 'Battle failed');
        setLoading(false);
        return;
      }

      // Store result for after battle animation
      setBattleResult(result.winner === 'player' ? 'victory' : 'defeat');

      // Set up battleState in context for BattleController and BattleScreen
      const opponentStats = opponent.pokemon.stats;
      setBattleState({
        player: {
          name: playerPokemon.name,
          primaryType: playerPokemon.type,
          stats: playerPokemon.stats,
          currentHP: playerPokemon.stats.HP,
          currentStamina: 100,
          moveStates: {},
          isResting: false,
          statusEffects: []
        },
        opponent: {
          name: opponent.pokemon.name,
          primaryType: opponent.pokemon.primaryType,
          stats: opponentStats,
          currentHP: opponentStats.HP,
          currentStamina: 100,
          moveStates: {},
          isResting: false,
          statusEffects: []
        },
        tick: 0,
        log: result.battleLog || [],
        displayLog: [],
        isTrialBattle: true,
        trialMemberName: currentMember?.name,
        winner: result.winner === 'player' ? 'player' : 'opponent',
        serverResult: true
      });

      setBattlePhase('battling');
    } catch (err) {
      console.error('Trial battle error:', err);
      setError(err.message || 'Failed to process battle');
    } finally {
      setLoading(false);
    }
  };

  const handleBattleComplete = async () => {
    // Clear battle state from context
    setBattleState(null);
    setBattlePhase('result');

    if (battleResult === 'victory') {
      // Add current Pokemon's base species to used list (prevents using same evolution chain)
      const baseSpecies = getBaseSpecies(playerPokemon.name);
      const updatedSpecies = [...(trialState.usedSpecies || []), baseSpecies];

      // Check if this was the last member
      if (currentMemberIndex >= 3) {
        // Trial complete! Award stamp and primos
        try {
          await apiAwardTrialStamp(trialState.regionKey, token);
          await refreshPrimos();
        } catch (err) {
          console.error('Failed to award stamp:', err);
        }
      }

      // Update trial state
      setTrialState({
        ...trialState,
        usedSpecies: updatedSpecies,
        lastResult: 'victory',
        trialComplete: currentMemberIndex >= 3
      });
    } else {
      // Defeat - trial ends
      setTrialState({
        ...trialState,
        lastResult: 'defeat',
        inProgress: false
      });
    }
  };

  const handleContinue = () => {
    if (battleResult === 'victory' && currentMemberIndex < 3) {
      // Move to next Elite 4 member
      setTrialState({
        ...trialState,
        currentMemberIndex: currentMemberIndex + 1,
        currentPokemon: null,
        opponent: null,
        inProgress: false
      });
      setGameState('trialDetail');
    } else {
      // Return to trials list
      setTrialState(null);
      setGameState('trials');
    }
  };

  // Loading state (includes waiting for data to load)
  if (!dataLoaded || loading || battlePhase === 'preparing') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-t-amber-500 border-r-red-500 border-b-orange-500 border-l-transparent"
          />
          <p className="text-white text-lg font-semibold">Preparing Battle...</p>
          <p className="text-gray-400 text-sm mt-1">
            {playerPokemon?.name} vs {opponent?.pokemon?.name}
          </p>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 max-w-sm w-full text-center"
        >
          <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-pocket-text mb-2">Battle Error</h2>
          <p className="text-pocket-text-light mb-4">{error}</p>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setError(null);
                setBattlePhase('preparing');
              }}
              className="flex-1 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
            >
              <RotateCcw size={18} />
              Retry
            </button>
            <button
              onClick={() => setGameState('trials')}
              className="flex-1 py-3 bg-gray-200 text-pocket-text rounded-xl font-semibold hover:bg-gray-300 transition-colors"
            >
              Back
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Battle animation phase - render BattleScreen (reads from context)
  if (battlePhase === 'battling' && battleState) {
    return <BattleScreen />;
  }

  // Result screen
  if (battlePhase === 'result') {
    const isVictory = battleResult === 'victory';
    const isTrialComplete = isVictory && currentMemberIndex >= 3;

    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: isVictory
            ? 'linear-gradient(135deg, #fbbf24, #f97316)'
            : 'linear-gradient(135deg, #374151, #111827)'
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          {/* Result header */}
          <div
            className="p-6 text-center"
            style={{
              background: isVictory
                ? 'linear-gradient(135deg, #fbbf24, #f97316)'
                : 'linear-gradient(135deg, #4b5563, #1f2937)'
            }}
          >
            {isTrialComplete ? (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-20 h-20 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center"
                >
                  <Award size={40} className="text-white" />
                </motion.div>
                <h1 className="text-3xl font-bold text-white mb-1">Trial Complete!</h1>
                <p className="text-white/80">{region?.name} Elite 4 Conquered</p>
              </>
            ) : isVictory ? (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-20 h-20 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center"
                >
                  <Trophy size={40} className="text-white" />
                </motion.div>
                <h1 className="text-3xl font-bold text-white mb-1">Victory!</h1>
                <p className="text-white/80">
                  {currentMember?.name} Defeated ({currentMemberIndex + 1}/4)
                </p>
              </>
            ) : (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-20 h-20 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center"
                >
                  <X size={40} className="text-white" />
                </motion.div>
                <h1 className="text-3xl font-bold text-white mb-1">Defeat</h1>
                <p className="text-white/80">Trial Ended</p>
              </>
            )}
          </div>

          {/* Result content */}
          <div className="p-6">
            {/* Battle summary */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2">
                  <PokemonSprite pokemonName={playerPokemon.name} size={64} useScale={false} />
                </div>
                <p className="font-semibold text-pocket-text text-sm">{playerPokemon.name}</p>
                <TypeBadge type={playerPokemon.type} size={12} />
              </div>

              <div className="text-2xl font-bold text-pocket-text-light">VS</div>

              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2">
                  {generatePokemonSprite(opponent.pokemon.primaryType, opponent.pokemon.name, 64, false)}
                </div>
                <p className="font-semibold text-pocket-text text-sm">{opponent.pokemon.name}</p>
                <TypeBadge type={opponent.pokemon.primaryType} size={12} />
              </div>
            </div>

            {/* Trial complete rewards */}
            {isTrialComplete && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 mb-4 border-2 border-amber-300"
              >
                <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2 justify-center">
                  <Sparkles size={18} className="text-amber-500" />
                  Rewards
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Award size={20} className="text-amber-500" />
                      <span className="text-pocket-text font-semibold">{region?.name} Stamp</span>
                    </div>
                    <span className="text-green-600 font-bold">Earned!</span>
                  </div>
                  <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Sparkles size={20} className="text-amber-500" />
                      <span className="text-pocket-text font-semibold">Primos</span>
                    </div>
                    <span className="text-amber-600 font-bold">+{trialRewards.primos.toLocaleString()}</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Progress indicator (for victory, not complete) */}
            {isVictory && !isTrialComplete && (
              <div className="bg-pocket-bg rounded-xl p-4 mb-4">
                <p className="text-center text-pocket-text-light text-sm mb-2">Trial Progress</p>
                <div className="flex items-center justify-center gap-2">
                  {[0, 1, 2, 3].map((idx) => (
                    <div
                      key={idx}
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        idx <= currentMemberIndex
                          ? 'bg-amber-500 text-white'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {idx < currentMemberIndex ? '✓' : idx + 1}
                    </div>
                  ))}
                </div>
                <p className="text-center text-pocket-text text-sm mt-3">
                  Next: {region?.members?.[currentMemberIndex + 1]?.name}
                </p>
              </div>
            )}

            {/* Defeat message */}
            {!isVictory && (
              <div className="bg-gray-100 rounded-xl p-4 mb-4 text-center">
                <p className="text-pocket-text-light text-sm">
                  You made it to battle {currentMemberIndex + 1} of 4.
                </p>
                <p className="text-pocket-text text-sm mt-1">
                  Train stronger Pokemon and try again!
                </p>
              </div>
            )}

            {/* Continue button */}
            <button
              onClick={handleContinue}
              className={`w-full py-4 rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                isVictory
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-lg'
                  : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:shadow-lg'
              }`}
            >
              {isVictory && !isTrialComplete ? (
                <>
                  Next Battle
                  <ChevronRight size={24} />
                </>
              ) : (
                'Continue'
              )}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return null;
};

export default TrialBattleScreen;
